import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ContentStrategy = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');
  if (!data) return null;

  const { contentScore = 70, contentGrade = 'B+', totalContentIdeas = 20, contentGap = '35%', contentPillars = [], contentCalendar = [], contentIdeas = [], contentGaps = [], contentPerformance = {}, aiOptimizationTips = [] } = data;
  const topFormats = contentPerformance.topFormats || [];
  const bestChannels = contentPerformance.bestChannels || [];

  const getTypeColor = (t) => ({ Blog: '#6366f1', Video: '#ef4444', Guide: '#10b981', Infographic: '#f59e0b', Podcast: '#8b5cf6', 'Case Study': '#ec4899', 'Blog Post': '#6366f1' }[t] || '#6366f1');
  const getPriorityColor = (p) => p === 'High' ? '#ef4444' : p === 'Medium' ? '#f59e0b' : '#10b981';
  const getDiffColor = (d) => d === 'Easy' ? '#10b981' : d === 'Medium' ? '#f59e0b' : '#ef4444';
  const getStatusColor = (s) => ({ Planned: '#6366f1', Draft: '#f59e0b', Review: '#8b5cf6', Published: '#10b981' }[s] || '#4b5563');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'ideas', label: 'Content Ideas' },
    { id: 'calendar', label: 'Calendar & Tips' }
  ];

  return (
    <div style={{ color: '#e5e7eb' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 600, background: activeTab === t.id ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(99,102,241,0.1)', color: activeTab === t.id ? '#fff' : '#6366f1', transition: 'all 0.2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            <div style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 14, padding: 20, textAlign: 'center', border: '1px solid rgba(99,102,241,0.2)' }}>
              <p style={{ color: '#d1d5db', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Content Score</p>
              <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto 8px' }}>
                <svg viewBox="0 0 90 90" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="45" cy="45" r="38" fill="none" stroke="rgba(75,85,99,0.3)" strokeWidth="7" />
                  <circle cx="45" cy="45" r="38" fill="none" stroke={contentScore >= 70 ? '#10b981' : contentScore >= 40 ? '#f59e0b' : '#ef4444'} strokeWidth="7" strokeDasharray={`${(contentScore/100)*239} 239`} strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: '#fff' }}>{contentScore}</span>
                </div>
              </div>
              <span style={{ color: contentScore >= 70 ? '#10b981' : '#f59e0b', fontWeight: 700, fontSize: 18 }}>{contentGrade}</span>
            </div>
            {[{ label: 'Content Ideas', value: totalContentIdeas, color: '#6366f1' }, { label: 'Content Gap', value: contentGap, color: '#ef4444' }, { label: 'Pillars', value: contentPillars.length, color: '#10b981' }].map((m, i) => (
              <div key={i} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 14, padding: 20, textAlign: 'center', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <p style={{ color: '#d1d5db', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{m.label}</p>
                <span style={{ fontSize: 30, fontWeight: 700, color: m.color }}>{m.value}</span>
              </div>
            ))}
          </div>

          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Content Pillars</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14, marginBottom: 28 }}>
            {contentPillars.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 12, padding: 18, borderLeft: `4px solid ${getPriorityColor(p.priority)}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{p.name}</span>
                  <span style={{ background: getPriorityColor(p.priority) + '22', color: getPriorityColor(p.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{p.priority}</span>
                </div>
                <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 8 }}>{p.description}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(p.topics || []).map((t, j) => (
                    <span key={j} style={{ background: 'rgba(99,102,241,0.1)', color: '#a5b4fc', padding: '3px 8px', borderRadius: 6, fontSize: 13 }}>{t}</span>
                  ))}
                </div>
                <p style={{ color: '#6366f1', fontSize: 14, marginTop: 6 }}>{p.contentCount} content pieces planned</p>
              </motion.div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }} aria-label="Top Formats section">Top Formats</p>
              {topFormats.map((f, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#fff', fontSize: 15, fontWeight: 500 }}>{f.format}</span>
                    <span style={{ color: '#6366f1', fontSize: 15, fontWeight: 600 }}>{f.effectiveness}%</span>
                  </div>
                  <div style={{ background: 'rgba(75,85,99,0.3)', borderRadius: 4, height: 6 }}>
                    <div style={{ background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', role: 'presentation', borderRadius: 4, height: '100%', width: `${f.effectiveness}%` }} />
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 14, marginTop: 2 }}>{f.recommendation}</p>
                </div>
              ))}
            </div>
            <div>
              <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }} aria-label="Best Channels section">Best Channels</p>
              {bestChannels.map((c, i) => (
                <div key={i} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 10, padding: 12, marginBottom: 8, border: '1px solid rgba(99,102,241,0.1)' }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 16, display: 'block', marginBottom: 6 }}>{c.channel}</span>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: '#d1d5db', fontSize: 13 }}>Reach</span>
                      <div style={{ background: 'rgba(75,85,99,0.3)', borderRadius: 3, height: 5, marginTop: 3 }}>
                        <div style={{ background: '#10b981', borderRadius: 3, height: '100%', width: `${c.reach}%` }} />
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: '#d1d5db', fontSize: 13 }}>Engagement</span>
                      <div style={{ background: 'rgba(75,85,99,0.3)', borderRadius: 3, height: 5, marginTop: 3 }}>
                        <div style={{ background: '#6366f1', borderRadius: 3, height: '100%', width: `${c.engagement}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {activeTab === 'ideas' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <p style={{ color: '#d1d5db', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }} aria-label="Content Ideas section">Content Ideas</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {contentIdeas.map((idea, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 12, padding: 18, borderLeft: `4px solid ${getTypeColor(idea.type)}`, border: '1px solid rgba(99,102,241,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{idea.title}</span>
                    <p style={{ color: '#d1d5db', fontSize: 14, marginTop: 4 }}>{idea.description}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 12 }}>
                    <span style={{ background: getTypeColor(idea.type) + '22', color: getTypeColor(idea.type), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{idea.type}</span>
                    <span style={{ background: getDiffColor(idea.difficulty) + '22', color: getDiffColor(idea.difficulty), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{idea.difficulty}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 14, marginBottom: 8 }}>
                  <span style={{ color: '#d1d5db' }}>Search Vol: <span style={{ color: idea.searchVolume === 'High' ? '#10b981' : '#f59e0b' }}>{idea.searchVolume}</span></span>
                  <span style={{ color: '#d1d5db' }}>AI Impact: <span style={{ color: idea.aiVisibilityImpact === 'High' ? '#10b981' : '#f59e0b' }}>{idea.aiVisibilityImpact}</span></span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(idea.targetKeywords || []).map((kw, j) => (
                    <span key={j} style={{ background: 'rgba(99,102,241,0.1)', color: '#a5b4fc', padding: '2px 8px', borderRadius: 6, fontSize: 13 }}>{kw}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginTop: 28, marginBottom: 14 }} aria-label="Content Gaps section">Content Gaps</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {contentGaps.map((g, i) => (
              <div key={i} style={{ background: 'rgba(239,68,68,0.06)', borderRadius: 12, padding: 16, border: '1px solid rgba(239,68,68,0.15)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{g.topic}</span>
                  <span style={{ background: getPriorityColor(g.estimatedImpact) + '22', color: getPriorityColor(g.estimatedImpact), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{g.estimatedImpact} Impact</span>
                </div>
                <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 4 }}>{g.opportunity}</p>
                <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <span style={{ color: '#6366f1' }}>Format: {g.suggestedFormat}</span>
                  <span style={{ color: '#f59e0b' }}>Competitor: {g.competitorCoverage}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {activeTab === 'calendar' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }} aria-label="Content Calendar section">Content Calendar</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(75,85,99,0.3)' }}>
                  {['Week', 'Type', 'Title', 'Keyword', 'Platform', 'Status'].map(h => (
                    <th scope="col" key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contentCalendar.map((item, i) => (
                  <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }} style={{ borderBottom: '1px solid rgba(75,85,99,0.15)' }}>
                    <td style={{ padding: '10px 12px', color: '#6366f1', fontWeight: 600 }}>{item.week}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: getTypeColor(item.contentType) + '22', color: getTypeColor(item.contentType), padding: '2px 8px', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>{item.contentType}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#fff', fontWeight: 500 }}>{item.title}</td>
                    <td style={{ padding: '10px 12px', color: '#a5b4fc', fontSize: 14 }}>{item.targetKeyword}</td>
                    <td style={{ padding: '10px 12px', color: '#d1d5db' }}>{item.platform}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: getStatusColor(item.status) + '22', color: getStatusColor(item.status), padding: '2px 8px', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>{item.status}</span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginTop: 28, marginBottom: 14 }} aria-label="AI Optimization Tips section">AI Optimization Tips</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            {aiOptimizationTips.map((tip, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 12, padding: 16, border: '1px solid rgba(99,102,241,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{tip.tip}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', padding: '2px 8px', borderRadius: 8, fontSize: 13 }}>{tip.category}</span>
                  <span style={{ background: getPriorityColor(tip.impact) + '22', color: getPriorityColor(tip.impact), padding: '2px 8px', borderRadius: 8, fontSize: 13 }}>{tip.impact} Impact</span>
                  <span style={{ background: getDiffColor(tip.implementation) + '22', color: getDiffColor(tip.implementation), padding: '2px 8px', borderRadius: 8, fontSize: 13 }}>{tip.implementation}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ContentStrategy;
